import React, { Component } from 'react';

// Components and Others
import PropTypes from 'prop-types';
import { Form, Col } from 'react-bootstrap';
import Select from 'components/widgets/Select';
import PortfolioSettingsStyles from 'styles/modules/settings/PortfolioSettingsStyles';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import PortfolioSettingsDialogContainer from 'components/containers/settings/portfolio/PortfolioSettingsDialogContainer';
import InputNumber from 'components/widgets/InputNumber';
import If from 'components/widgets/conditional/If';
import UserContext from 'components/context/UserContext';

import { isValidField, isValidSchema } from 'utils/schema/utils';
import { PortfolioSettingSchema } from 'utils/schema/settings/PortfolioSettings';
import PortfolioTabs from 'utils/enum/PortfolioEnum';
import PortfolioHistoryTab from 'components/modules/settings/bphp/portfolio/PortfolioHistoryTab';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import {
    DialogContent, Dialog, Grid, Tabs, Tab, Box,
} from '@material-ui/core';

const styles = (theme) => PortfolioSettingsStyles.referenceDialog(theme);

class PortfolioSettingsDialog extends Component {
    static contextType = UserContext;

    getCreditCompanies() {
        const {
            props: {
                listCreditCompany, record: {
                    creditReportingCompanyId,
                },
            },
        } = this;

        const listCreditCompanyActive = listCreditCompany.filter(
            (item) => item.active
                || item.creditReportingId === creditReportingCompanyId,
        );

        return listCreditCompanyActive.map((item) => ({ value: item.creditReportingId, label: item.furnisherName }));
    }

    getAvailableLots() {
        const { userInformation } = this.context;
        const availableLots = userInformation?.lots || [];

        if (!availableLots?.length === 0) {
            return [];
        }

        return availableLots.map((lot) => ({
            value: lot.lotId,
            label: lot.lotName,
        }));
    }

    render() {
        const {
            props: {
                classes, onSave, open, onClose,
                onChangeValue, isEditing, record,
                onTabChange, selectedTab,
            },
        } = this;
        const prefixTitle = isEditing ? 'Edit' : 'New';

        const isValidData = isValidSchema(PortfolioSettingSchema, record);
        const { isValid, errors } = isValidData;

        return (
            <Dialog
                open={open}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    className: classes.dialog,
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogAppBar title={`${prefixTitle} Portfolio`} onClose={onClose} iconSize="sm" />
                <DialogContent>
                    <Tabs
                        value={selectedTab}
                        onChange={onTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                    >
                        <Tab label="Detail" />
                        {isEditing
                            && (
                                <Tab label="Notes" />
                            )}
                    </Tabs>
                    <If condition={selectedTab === PortfolioTabs.DETAIL}>
                        <Box className={classes.dialogContent}>
                            <Grid container spacing={1} className="am-form">
                                <Grid item xs={6}>
                                    <Form.Group as={Col}>
                                        <Form.Label>Portfolio Name</Form.Label>
                                        <Form.Control
                                            className={isValidField(errors, 'name') ? 'invalid-field' : ''}
                                            value={record.name}
                                            type="text"
                                            onChange={(e) => onChangeValue('name', e.target.value)}
                                        />
                                    </Form.Group>
                                </Grid>
                                <Grid item xs={6}>
                                    <Form.Group as={Col}>
                                        <Form.Label>Credit Company</Form.Label>
                                        <Select
                                            name="creditReportingCompanyId"
                                            value={record.creditReportingCompanyId}
                                            options={this.getCreditCompanies()}
                                            placeholder="Select"
                                            maxMenuHeight={140}
                                            onChange={onChangeValue}
                                        />
                                    </Form.Group>
                                </Grid>
                                <Grid item xs={6}>
                                    <Form.Group as={Col}>
                                        <Form.Label>Default Lot Name</Form.Label>
                                        <Select
                                            name="lotId"
                                            value={record.lotId}
                                            options={this.getAvailableLots()}
                                            placeholder="Select"
                                            maxMenuHeight={140}
                                            onChange={onChangeValue}
                                        />
                                    </Form.Group>
                                </Grid>
                                <Grid item xs={12}>
                                    <Form.Group as={Col}>
                                        <Form.Label className={classes.subtitle}>Convenience Fees</Form.Label>
                                        <div className={classes.legend} />
                                    </Form.Group>
                                </Grid>
                                <Grid item xs={3}>
                                    <Form.Group as={Col}>
                                        <Form.Label>Credit Card</Form.Label>
                                        <InputNumber
                                            decimalScale={2}
                                            value={record.convenienceFee}
                                            onChange={(value) => onChangeValue('convenienceFee', value)}
                                        />
                                    </Form.Group>
                                </Grid>
                                <Grid item xs={3}>
                                    <Form.Group as={Col}>
                                        <Form.Label>ACH</Form.Label>
                                        <InputNumber
                                            decimalScale={2}
                                            value={record.achConvenienceFee}
                                            onChange={(value) => onChangeValue('achConvenienceFee', value)}
                                        />
                                    </Form.Group>
                                </Grid>
                            </Grid>
                        </Box>
                    </If>
                    <If condition={selectedTab === PortfolioTabs.NOTES}>
                        <Box className={classes.dialogContent}>
                            <Grid container spacing={1} className="am-form">
                                <Box className={classes.notes}>
                                    <PortfolioHistoryTab portfolioId={parseInt(record.portfolioId, 10)} className={classes.historyTab} />
                                </Box>
                            </Grid>
                        </Box>
                    </If>
                </DialogContent>
                <DialogActions
                    disablePrimaryButton={!isValid}
                    onClickSecondary={onClose}
                    onClickPrimary={() => onSave(record)}
                />
            </Dialog>
        );
    }
}

PortfolioSettingsDialog.propTypes = {
    classes: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onChangeValue: PropTypes.func.isRequired,
    record: PropTypes.object.isRequired,
    isEditing: PropTypes.bool.isRequired,
    onTabChange: PropTypes.func.isRequired,
    selectedTab: PropTypes.number.isRequired,
    listCreditCompany: PropTypes.array.isRequired,
};

export default withStyles(styles)(PortfolioSettingsDialogContainer(PortfolioSettingsDialog));
